import {observer} from 'mobx-react-lite'
import * as React from 'react'
import tw from 'tailwind-styled-components'
import {Lang} from 'yougile-lang'
import {Checklist, DataObjectId, Hub} from 'yougile-model'
import {useDraggable} from '@containers/hooks-draggable'
import {cn} from '@containers/html-utils'
import {isSafari} from '@extensions/platform'
import {Task} from '@extensions/task'
import {useChecklistsStore} from '../checklists-store'
import {createDragImage} from '../createDragImage'
import {LinkButtonAdd} from '../link-button-add'
import {Header} from '../subtasks-new/header'
import {ItemFullId} from '../types'
import {ChecklistControl} from './checklist/checklist-control'

interface Props {
  hub: Hub | undefined
  hideSubtasks?: boolean
}

const dragImage = createDragImage()
const TwContainer = tw.div<{
  $isTaskTemplate?: boolean
  $showSubtasks?: boolean
}>`
  ${p => {
    const cls = []

    if (p.$isTaskTemplate) {
      if (p.$showSubtasks) {
        cls.push('pt-24')
      }
    } else {
      if (p.$showSubtasks) {
        cls.push('pt-32')
      }
      cls.push('pb-32')
    }

    return cls.join(' ')
  }}
`

const getChecklistCls = (isDragged: boolean, index: number) => {
  if (!isDragged) {
    return index > 0 ? 'mt-32' : ''
  }
  if (index === 0) {
    return ''
  }

  return 'mt-8 mb-8'
}

export const CheckList = observer((props: Props) => {
  const {hub, hideSubtasks} = props
  const [state, store] = useChecklistsStore()

  const checklistDragCtl = useDraggable<DataObjectId>({
    onDragStart: store.onDragStart,
    onDragEnd: store.onDragEnd,
    onDragOver: store.onDrag,
    ghost: dragImage
  })
  const checklistItemDragCtl = useDraggable<ItemFullId>({
    onDragStart: store.onDragItemStart,
    onDragEnd: store.onDragItemEnd,
    onDragOver: store.onDragItem,
    ghost: isSafari() ? dragImage : undefined
  })

  const isTaskTemplate = Task.isTaskTemplate(hub)
  const needPaddings = !isTaskTemplate

  return (
    <TwContainer
      id={'checklist-container'}
      $isTaskTemplate={isTaskTemplate}
      $showSubtasks={!hideSubtasks}
    >
      <Header
        title={Lang.msg.checklists}
        help={'checklists'}
        className={isTaskTemplate ? 'mb-4 pb-4 pt-8 h-28' : 'mb-8 mx-16'}
      />

      {hub && !!state.checklists.length && (
        <>
          {state.checklists.map(
            (list, index) =>
              isChecklist(list) && (
                <ChecklistControl
                  key={list.id}
                  task={hub}
                  list={list}
                  checklistDragCtl={checklistDragCtl}
                  checklistItemDragCtl={checklistItemDragCtl}
                  className={getChecklistCls(
                    !!checklistDragCtl.draggedId,
                    index
                  )}
                  needPaddings={needPaddings}
                />
              )
          )}
        </>
      )}

      {state.allowChecklists && (
        <div
          className={cn(
            needPaddings ? 'mx-16' : '',
            state.checklists.length
              ? 'pt-16 shadow-border-t-1 shadow-grey-l60'
              : 'mt-16'
          )}
        >
          <LinkButtonAdd
            onMouseDown={store.add}
            text={Lang.msg.addChecklist}
            type={'checklist'}
          />
        </div>
      )}
    </TwContainer>
  )
})

const isChecklist = (item: Checklist): boolean => !!item?.id && !!item.title
